import { useState } from "react"
import { useFirestore } from "reactfire"
import { collection, addDoc, doc, updateDoc } from "firebase/firestore"
import { CATEGORIAS_EVENTO } from "../config/categoriasEvento"

export default function CalendarioModal({ unidadId, fecha, evento, onCerrar, onGuardarOverride }) {
  const firestore = useFirestore()
  const editando  = !!evento

  const fechaInicial = fecha
    ? `${fecha.getFullYear()}-${String(fecha.getMonth()+1).padStart(2,"0")}-${String(fecha.getDate()).padStart(2,"0")}`
    : ""

  const [form, setForm] = useState({
    titulo:      evento?.titulo      || "",
    descripcion: evento?.descripcion || "",
    categoria:   evento?.categoria   || "normal",
    fechaInicio: evento?.fechaInicio || fechaInicial,
    fechaFin:    evento?.fechaFin    || fechaInicial,
    todoElDia:   evento?.todoElDia   !== false,
    horaInicio:  evento?.horaInicio  || "09:00",
    horaFin:     evento?.horaFin     || "18:00",
  })
  const [guardando, setGuardando] = useState(false)
  const [error,     setError]     = useState("")

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }))
    setError("")
  }

  const guardar = async () => {
    if (!form.titulo.trim())    { setError("El título es obligatorio."); return }
    if (!form.fechaInicio)      { setError("La fecha de inicio es obligatoria."); return }
    if (!form.categoria)        { setError("Selecciona una categoría."); return }
    if (form.fechaFin < form.fechaInicio) { setError("La fecha de fin no puede ser anterior al inicio."); return }

    setGuardando(true)
    setError("")

    const data = {
      titulo:      form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      categoria:   form.categoria,
      fechaInicio: form.fechaInicio,
      fechaFin:    form.fechaFin || form.fechaInicio,
      todoElDia:   form.todoElDia,
      horaInicio:  form.todoElDia ? null : form.horaInicio,
      horaFin:     form.todoElDia ? null : form.horaFin,
      unidadId,
    }

    try {
      if (onGuardarOverride) {
        // Modo Consejo: guarda en todas las unidades
        await onGuardarOverride(data)
      } else if (editando) {
        await updateDoc(doc(firestore, "unidades", unidadId, "eventos", evento.id), data)
      } else {
        await addDoc(collection(firestore, "unidades", unidadId, "eventos"), {
          ...data,
          creadoEn: { seconds: Math.floor(Date.now() / 1000) },
        })
      }
      onCerrar()
    } catch (err) {
      setError("Error al guardar: " + err.message)
    } finally {
      setGuardando(false)
    }
  }

  const categActual = CATEGORIAS_EVENTO.find(c => c.value === form.categoria)

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box" style={{ maxWidth: 520 }}>
        <button className="modal-cerrar" onClick={onCerrar}>×</button>
        <h2 className="modal-titulo">
          {editando ? "✏️ Editar evento" : "📅 Nuevo evento"}
        </h2>

        {/* Título */}
        <div className="form-group">
          <label className="form-label">Título del evento</label>
          <input
            className="form-input" name="titulo" value={form.titulo}
            onChange={handleChange} placeholder="Ej: Reunión mensual, Campamento de verano..."
          />
        </div>

        {/* Categoría */}
        <div className="form-group">
          <label className="form-label">Categoría</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CATEGORIAS_EVENTO.map(cat => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, categoria: cat.value }))}
                style={{
                  padding: "7px 14px", borderRadius: 10, border: "2px solid",
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600, fontSize: 13, transition: "all 0.15s",
                  borderColor: form.categoria === cat.value ? cat.color : "var(--gris-borde)",
                  background:  form.categoria === cat.value ? cat.bg    : "#fff",
                  color:       form.categoria === cat.value ? cat.color : "var(--gris)",
                }}
              >
                {cat.icono} {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fechas */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Fecha inicio</label>
            <input
              className="form-input" type="date" name="fechaInicio"
              value={form.fechaInicio} onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Fecha fin</label>
            <input
              className="form-input" type="date" name="fechaFin"
              value={form.fechaFin} min={form.fechaInicio} onChange={handleChange}
            />
          </div>
        </div>

        {/* Todo el día */}
        <label className={`exento-toggle ${form.todoElDia ? "activo" : ""}`} style={{ marginBottom: 16 }}>
          <input type="checkbox" name="todoElDia" checked={form.todoElDia} onChange={handleChange} />
          <div>
            <strong>Todo el día</strong>
            <div style={{ fontSize: 12, color: "var(--gris)", marginTop: 2 }}>
              Desactiva para especificar horario
            </div>
          </div>
        </label>

        {/* Horario — solo si no es todo el día */}
        {!form.todoElDia && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Hora inicio</label>
              <input
                className="form-input" type="time" name="horaInicio"
                value={form.horaInicio} onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Hora fin</label>
              <input
                className="form-input" type="time" name="horaFin"
                value={form.horaFin} onChange={handleChange}
              />
            </div>
          </div>
        )}

        {/* Descripción */}
        <div className="form-group">
          <label className="form-label">
            Descripción <span style={{ fontWeight: 400, color: "var(--gris)" }}>(opcional)</span>
          </label>
          <textarea
            className="form-input" name="descripcion" value={form.descripcion}
            onChange={handleChange} rows={3} style={{ resize: "vertical", minHeight: 72 }}
            placeholder="Detalles del evento, lugar, qué llevar..."
          />
        </div>

        {error && <div style={{ color: "var(--rojo)", fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}

        <div className="form-actions">
          <button className="btn-secondary" onClick={onCerrar}>Cancelar</button>
          <button
            className="btn-primary"
            onClick={guardar}
            disabled={guardando}
            style={categActual ? { background: categActual.color } : {}}
          >
            {guardando ? "Guardando..." : editando ? "Guardar cambios" : "Crear evento"}
          </button>
        </div>
      </div>
    </div>
  )
}
